import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { createRecurringSeriesCore } from '@/lib/recurring-utils';

export interface AnnualBudget {
  id: string;
  user_id: string;
  year: number;
  name: string;
  total_income_expected: number;
  notes: string | null;
  created_at: string;
  updated_at: string;
}

export interface BudgetItem {
  id: string;
  budget_id: string;
  category_id: string | null;
  goal_id: string | null;
  recurrence_id: string | null;
  name: string;
  item_type: 'expense' | 'income';
  frequency: 'monthly' | 'specific_months' | 'annual';
  active_months: number[];
  monthly_amount: number;
  is_fixed: boolean;
  provision_enabled: boolean;
  provision_months: number;
  notes: string | null;
  sort_order: number;
  created_at: string;
  updated_at: string;
  category?: {
    id: string;
    name: string;
    icon: string;
    color: string;
  };
  goal?: {
    id: string;
    name: string;
    current_amount: number;
    target_amount: number;
    target_date: string | null;
  };
  recurrence?: {
    id: string;
    frequency: string;
    start_date: string;
    end_date: string | null;
    occurrences: number | null;
  };
  month_values?: BudgetMonthValue[];
}

export interface BudgetMonthValue {
  id: string;
  budget_item_id: string;
  month: number;
  planned_amount: number;
  actual_amount: number;
  status: 'pending' | 'partial' | 'complete' | 'over';
  notes: string | null;
  updated_at: string;
}

export function useAnnualBudget(year: number) {
  const [budget, setBudget] = useState<AnnualBudget | null>(null);
  const [items, setItems] = useState<BudgetItem[]>([]);
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();

  const fetchBudget = async () => {
    try {
      setLoading(true);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      // Fetch or create budget for the year
      let { data: budgetData, error } = await supabase
        .from('annual_budgets')
        .select('*')
        .eq('year', year)
        .eq('user_id', user.id)
        .single();

      if (error && error.code === 'PGRST116') {
        // No budget exists, create one
        const { data: newBudget, error: createError } = await supabase
          .from('annual_budgets')
          .insert({ user_id: user.id, year, name: `Orçamento ${year}` })
          .select()
          .single();

        if (createError) throw createError;
        budgetData = newBudget;
      } else if (error) {
        throw error;
      }

      setBudget(budgetData);

      if (budgetData) {
        // Fetch items with categories, goals, and month values
        const { data: itemsData, error: itemsError } = await supabase
          .from('budget_items')
          .select(`
            *,
            category:categories(id, name, icon, color),
            goal:goals(id, name, current_amount, target_amount, target_date),
            recurrence:recurrences(id, frequency, start_date, end_date, occurrences),
            month_values:budget_month_values(*)
          `)
          .eq('budget_id', budgetData.id)
          .order('sort_order');

        if (itemsError) throw itemsError;
        
        // Cast the data to match our BudgetItem interface
        const typedItems = (itemsData || []).map(item => ({
          ...item,
          item_type: item.item_type as 'expense' | 'income',
          frequency: item.frequency as 'monthly' | 'specific_months' | 'annual',
          goal: item.goal || undefined,
          recurrence: item.recurrence || undefined,
          month_values: (item.month_values || []).map((mv: any) => ({
            ...mv,
            status: mv.status as 'pending' | 'partial' | 'complete' | 'over',
          })),
        })) as BudgetItem[];
        
        setItems(typedItems);
      }
    } catch (error: any) {
      toast({
        title: 'Erro ao carregar orçamento',
        description: error.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const createItem = async (item: Partial<BudgetItem> & { auto_create_goal?: boolean; auto_create_recurring?: boolean; account_id?: string }) => {
    if (!budget) return null;

    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Usuário não autenticado');

      let goalId = item.goal_id;
      let recurrenceId = item.recurrence_id;

      // Auto-create goal if requested
      if (item.provision_enabled && item.auto_create_goal && !item.goal_id) {
        const targetMonth = item.active_months?.[0] || 1;
        const targetDate = new Date(year, targetMonth - 1, 15);

        const { data: newGoal, error: goalError } = await supabase
          .from('goals')
          .insert({
            user_id: user.id,
            name: item.name || 'Provisão',
            type: 'simple_savings',
            target_amount: item.monthly_amount || 0,
            current_amount: 0,
            target_date: targetDate.toISOString().split('T')[0],
          })
          .select()
          .single();

        if (goalError) throw goalError;
        goalId = newGoal.id;

        toast({
          title: 'Cofrinho criado!',
          description: `"${item.name}" foi criado automaticamente`,
        });
      }

      // Auto-create recurring series if requested
      if (item.auto_create_recurring && !recurrenceId && item.account_id) {
        const startMonth = item.active_months?.[0] || new Date().getMonth() + 1;
        const startDate = new Date(year, startMonth - 1, 1);
        const startDateStr = startDate.toISOString().split('T')[0];

        const result = await createRecurringSeriesCore({
          userId: user.id,
          description: item.name || 'Recorrência',
          type: item.item_type || 'expense',
          totalAmount: item.monthly_amount || 0,
          categoryId: item.category_id || null,
          accountId: item.account_id,
          startDate: startDateStr,
          recurrenceType: 'fixed',
          frequency: 'monthly',
        });

        recurrenceId = result.recurrenceId;

        toast({
          title: 'Série recorrente criada!',
          description: `12 transações de "${item.name}" foram geradas automaticamente`,
        });
      }

      const insertData = {
        budget_id: budget.id,
        name: item.name || '',
        item_type: item.item_type || 'expense',
        frequency: item.frequency || 'monthly',
        active_months: item.active_months || [1,2,3,4,5,6,7,8,9,10,11,12],
        monthly_amount: item.monthly_amount || 0,
        is_fixed: item.is_fixed || false,
        provision_enabled: item.provision_enabled || false,
        provision_months: item.provision_months || 12,
        category_id: item.category_id,
        goal_id: goalId,
        recurrence_id: recurrenceId,
        notes: item.notes,
      };

      const { data, error } = await supabase
        .from('budget_items')
        .insert(insertData)
        .select(`
          *,
          category:categories(id, name, icon, color)
        `)
        .single();

      if (error) throw error;

      // Create month values for active months
      if (data && item.active_months) {
        const monthValues = item.active_months.map(month => ({
          budget_item_id: data.id,
          month,
          planned_amount: item.frequency === 'monthly' ? item.monthly_amount : 
            (item.frequency === 'annual' && month === item.active_months?.[0]) ? item.monthly_amount : 0,
        }));

        await supabase.from('budget_month_values').insert(monthValues);
      }

      await fetchBudget();
      toast({ title: 'Item criado com sucesso' });
      return data;
    } catch (error: any) {
      toast({
        title: 'Erro ao criar item',
        description: error.message,
        variant: 'destructive',
      });
      return null;
    }
  };

  const updateItem = async (id: string, updates: Partial<BudgetItem> & { recurrence_id?: string | null }) => {
    try {
      // Get existing item to check for goal/recurrence sync
      const existingItem = items.find(i => i.id === id);

      // Separate recurrence_id from updates if provided
      const { recurrence_id, budget_id: _stripBudgetId, ...restUpdates } = updates as any;
      const dbUpdates = recurrence_id !== undefined
        ? { ...restUpdates, recurrence_id }
        : restUpdates;
      
      const { error } = await supabase
        .from('budget_items')
        .update(dbUpdates)
        .eq('id', id);

      if (error) throw error;

      // Sync goal target_amount if monthly_amount changed and item has a linked goal
      if (updates.monthly_amount !== undefined && existingItem?.goal_id) {
        const { error: goalError } = await supabase
          .from('goals')
          .update({ target_amount: updates.monthly_amount })
          .eq('id', existingItem.goal_id);

        if (!goalError) {
          toast({ title: 'Cofrinho sincronizado com novo valor' });
        }
      }

      // Check if recurrence sync is needed (handled by caller via pendingRecurrenceSync)
      const needsRecurrenceSync =
        updates.monthly_amount !== undefined &&
        existingItem?.recurrence_id != null &&
        updates.monthly_amount !== existingItem.monthly_amount;

      await fetchBudget();
      toast({ title: 'Item atualizado com sucesso' });

      return { needsRecurrenceSync, recurrenceId: existingItem?.recurrence_id };
    } catch (error: any) {
      toast({
        title: 'Erro ao atualizar item',
        description: error.message,
        variant: 'destructive',
      });
      return { needsRecurrenceSync: false };
    }
  };

  const deleteItem = async (id: string) => {
    try {
      const { error } = await supabase
        .from('budget_items')
        .delete()
        .eq('id', id);

      if (error) throw error;
      await fetchBudget();
      toast({ title: 'Item removido com sucesso' });
    } catch (error: any) {
      toast({
        title: 'Erro ao remover item',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const updateMonthValue = async (
    itemId: string,
    month: number,
    updates: Partial<BudgetMonthValue>
  ) => {
    try {
      const { error } = await supabase
        .from('budget_month_values')
        .upsert({
          budget_item_id: itemId,
          month,
          ...updates,
        }, { onConflict: 'budget_item_id,month' });

      if (error) throw error;
      await fetchBudget();
    } catch (error: any) {
      toast({
        title: 'Erro ao atualizar valor',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  const updateBudget = async (updates: Partial<AnnualBudget>) => {
    if (!budget) return;

    try {
      const { error } = await supabase
        .from('annual_budgets')
        .update(updates)
        .eq('id', budget.id);

      if (error) throw error;
      setBudget({ ...budget, ...updates });
    } catch (error: any) {
      toast({
        title: 'Erro ao atualizar orçamento',
        description: error.message,
        variant: 'destructive',
      });
    }
  };

  useEffect(() => {
    fetchBudget();
  }, [year]);

  return {
    budget,
    items,
    loading,
    createItem,
    updateItem,
    deleteItem,
    updateMonthValue,
    updateBudget,
    refetch: fetchBudget,
  };
}
